import React, { useState } from 'react'
import { useMutation } from 'react-query'

import { useApi } from '../../../hooks'

// "Requires Attention" — safety feature questions surfaced by adasThink.
// Answering a question cascades service status changes on the report
// (equipped → required, not-equipped → hidden, null → potential).
const SafetyFeatures = ({ report, refetch }) => {
  const { adasReportService } = useApi()
  const [pendingId, setPendingId] = useState(null)

  // Match Admin portal behavior: question list is based on services, not the
  // raw safetyFeatures array. This avoids over-rendering unrelated features.
  const services = (report.adas && report.adas.services) || []
  const potentialSensors = []
  const seen = new Set()
  services.forEach(svc => {
    if ((svc.recommendedStatus === 'potential' || svc.sensorAnswer) && !seen.has(svc.sensorName)) {
      seen.add(svc.sensorName)
      potentialSensors.push(svc.sensorName)
    }
  })

  const mutation = useMutation(
    ({ sensorName, answer }) => adasReportService.answerSensorQuestion({ id: report._id, sensorName, answer }),
    {
      onSettled: () => {
        setPendingId(null)
        if (refetch) refetch()
      },
    }
  )

  if (!potentialSensors.length) return null

  const setAnswer = (sensorName, answer) => {
    if (mutation.isLoading) return
    const currentService = services.find(s => s.sensorName === sensorName)
    const currentAnswer = (currentService && currentService.sensorAnswer) || null
    if (currentAnswer === answer) return
    setPendingId(sensorName)
    mutation.mutate({ sensorName, answer })
  }

  return (
    <div className='adas-detail__attention-card'>
      <div className='adas-detail__attention-header'>
        <h3 className='adas-detail__attention-title'>Requires Attention</h3>
        <p className='adas-detail__attention-subtitle'>
          Answering these questions will determine if &quot;Potential&quot; services are required.
        </p>
      </div>

      {potentialSensors.map((sensorName, idx) => {
        const currentService = services.find(s => s.sensorName === sensorName)
        const currentAnswer = (currentService && currentService.sensorAnswer) || null
        const isPending = mutation.isLoading && pendingId === sensorName
        const noAnswer = currentAnswer == null
        return (
          <div key={sensorName} className='adas-detail__attention-question'>
            <span className='adas-detail__question-text'>
              {idx + 1}. Is the vehicle equipped with {sensorName}?
            </span>
            <div className='adas-detail__question-actions'>
              <div className='adas-detail__radio-group'>
                <label className='adas-detail__radio'>
                  <input
                    type='radio'
                    name={`question-${sensorName}`}
                    checked={currentAnswer === 'equipped'}
                    disabled={isPending}
                    onChange={() => setAnswer(sensorName, 'equipped')}
                  />
                  <span>Yes</span>
                </label>
                <label className='adas-detail__radio'>
                  <input
                    type='radio'
                    name={`question-${sensorName}`}
                    checked={currentAnswer === 'not-equipped'}
                    disabled={isPending}
                    onChange={() => setAnswer(sensorName, 'not-equipped')}
                  />
                  <span>No</span>
                </label>
              </div>
              <span
                className={`adas-detail__reset-link${noAnswer ? ' is-disabled' : ''}`}
                onClick={() => !noAnswer && setAnswer(sensorName, null)}
              >
                Reset
              </span>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default SafetyFeatures
